import { hostnameMatchesDomainList } from '../../utils/hostname-matches-domains';
import { isSiblingSubdomain } from '../external-link-cleaner-domains';
import { removeTrackingParams } from './remove-tracking-params';

export interface ShouldCleanExternalLinkOptions {
  link: HTMLAnchorElement;
  appDomain: string;
  excludeDomains?: readonly string[];
  skipDomainFilter?: boolean;
}

export interface HandleExternalLinkClickOptions {
  event: MouseEvent;
  link: HTMLAnchorElement;
  trackingParams: readonly string[];
  appDomain: string;
  excludeDomains?: readonly string[];
  skipDomainFilter?: boolean;
}

export function shouldCleanExternalLink({
  link,
  appDomain,
  excludeDomains = [],
  skipDomainFilter = false
}: ShouldCleanExternalLinkOptions): boolean {
  if (link.hasAttribute('download')) {
    return false;
  }

  if (link.target !== '_blank' || !link.href.startsWith('http')) {
    return false;
  }

  try {
    const targetHostname = new URL(link.href).hostname;
    const normalizedAppDomain = appDomain.toLowerCase();

    if (targetHostname.toLowerCase() === normalizedAppDomain) {
      return false;
    }

    if (skipDomainFilter) {
      return true;
    }

    if (excludeDomains.length > 0) {
      return hostnameMatchesDomainList(targetHostname, excludeDomains);
    }

    return isSiblingSubdomain(targetHostname, appDomain);
  } catch {
    return false;
  }
}

export function handleExternalLinkClick({
  event,
  link,
  trackingParams,
  appDomain,
  excludeDomains = [],
  skipDomainFilter = false
}: HandleExternalLinkClickOptions): boolean {
  if (
    !shouldCleanExternalLink({
      link,
      appDomain,
      excludeDomains,
      skipDomainFilter
    })
  ) {
    return false;
  }

  event.preventDefault();

  const cleanUrl = removeTrackingParams(link.href, trackingParams);

  link.ownerDocument.defaultView?.open(
    cleanUrl,
    '_blank',
    'noopener,noreferrer'
  );

  return true;
}
